import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth, kmsService } from '@aaas/shared';

interface AccessMatrixUser {
  id: number;
  display_name: string | null;
  email: string;
  avatar_url: string | null;
}

interface AccessMatrixSpace {
  id: string;
  name: string;
}

interface AccessMatrixPagination {
  page: number;
  limit: number;
  total_users: number;
  total_pages: number;
}

interface AccessMatrixData {
  users: AccessMatrixUser[];
  spaces: AccessMatrixSpace[];
  access_matrix: Record<string, boolean>;
  pagination: AccessMatrixPagination;
}

interface BulkAccessChange {
  user_id: number;
  space_id: string;
  grant: boolean;
}

export function useAccessMatrixQuery(integrationId: string, page: number = 1, limit: number = 5) {
  const { idToken } = useAuth();

  return useQuery({
    queryKey: ['access-matrix', integrationId, page, limit],
    queryFn: async () => {
      if (!idToken) {
        throw new Error('Not authenticated');
      }
      const response = await kmsService.getAccessMatrix(idToken, integrationId, page, limit);
      return response.data as AccessMatrixData;
    },
    enabled: !!idToken && !!integrationId,
    staleTime: 30000, // 30 seconds
  });
}

export function useBulkUpdateAccessMutation(integrationId: string) {
  const { idToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (changes: BulkAccessChange[]) => {
      if (!idToken) {
        throw new Error('Not authenticated');
      }
      return await kmsService.bulkUpdateAccess(idToken, integrationId, changes);
    },
    onSuccess: () => {
      // Invalidate all access matrix queries for this integration
      queryClient.invalidateQueries({
        queryKey: ['access-matrix', integrationId],
      });

      // Also invalidate slack users list as accessible_spaces may have changed
      queryClient.invalidateQueries({
        queryKey: ['slack-users', integrationId],
      });
    },
  });
}
